import { alertVariants } from "@visiblethread/react-components";
import localhost, { downloadFileFromResponse } from "apis/localhost";
import {
  permissionsChangedSelfModal,
  userAdminImportUsersFromCsvConfirmationModal,
} from "components/Modal/confModalTypes";
import {
  MULTIPART_FORM_DATA_CONTENT_TYPE,
  URLENCODED_FORM_CONTENT_TYPE,
} from "constants/app";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  clearJobQueue,
  currentSessionInfo,
  pageLoading,
  renameSessionWorkspace,
  showGlobalAlert,
} from "slices/app/appSlice";
import { removeErrorMessage } from "slices/app/errorSlice";
import {
  setWorkspaceBannerProperties as setWorkspaceBannerPropertiesAction,
  setWorkspacesInfo,
} from "slices/systemAdmin/systemAdminSlice";

const getAddUsersToWorkspaceResultBanner = (
  users,
  workspaceId,
  currentLicensedUsers,
  response
) => {
  const username = users[0];
  const workspaceName = response.workspaces.find(
    (workspace) => workspace.id === workspaceId
  ).name;
  const workspaceReservedLicenses = response.workspaces.find(
    (workspace) => workspace.id === workspaceId
  ).reservedLicenses;
  const { licensed } = response.workspaces
    .find((workspace) => workspace.id === workspaceId)
    .users.find((user) => user.username === username);

  if (licensed) {
    return {
      variant: alertVariants.success,
      text: `The selected user/s have been successfully added to the ${workspaceName} workspace as Licensed user/s.`,
    };
  }
  if (workspaceReservedLicenses - currentLicensedUsers - users.length < 0) {
    return {
      variant: alertVariants.warning,
      text: `The selected user/s have been successfully added to the ${workspaceName} workspace but have not been licensed. You have reached the maximum number of licensed users for this workspace. Please de-activate a different user and try again.`,
    };
  }
  return {
    variant: alertVariants.warning,
    text: `The selected user/s have been successfully added to the ${workspaceName} workspace but have not been licensed. You have reached the maximum number of licensed users. Please de-activate a different user and try again.`,
  };
};

export const fetchWorkspaces = () => async (dispatch, getState) => {
  dispatch(pageLoading(true));
  const { adminType } = getState().systemAdmin;
  await localhost
    .get(`/${adminType}/workspaces`)
    .then(async (response) => {
      const { data } = response;
      dispatch(setWorkspacesInfo(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const getWorkspaceBannerProperties = (workspaceId) => (dispatch) => {
  dispatch(pageLoading(true));
  return localhost
    .get(`/systemAdmin/workspace/${workspaceId}/bannerProperties`)
    .then(async (response) => {
      return response.data;
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const setWorkspaceBannerProperties =
  (workspaceId, properties) => async (dispatch, getState) => {
    dispatch(pageLoading(true));
    await localhost
      .post(`/systemAdmin/workspace/${workspaceId}/bannerProperties`, {
        ...properties,
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(
          setWorkspaceBannerPropertiesAction({
            id: workspaceId,
            message: data.message,
            enabled: data.enabled,
            colour: data.colour,
            background: data.background,
          })
        );

        if (
          parseInt(getState().app?.session?.userInfo?.workspaceId, 10) ===
          workspaceId
        ) {
          dispatch(
            currentSessionInfo({
              ...getState().app?.session,
              userInfo: {
                ...getState().app?.session?.userInfo,
                bannerProperties: {
                  message: data.message,
                  enabled: data.enabled,
                  colour: data.colour,
                  background: data.background,
                },
              },
            })
          );
        }
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const addUsersToWorkspace =
  (users, currentLicensedUsers, workspaceId) => async (dispatch) => {
    dispatch(pageLoading(true));

    await localhost
      .put(`/systemAdmin/workspace/${workspaceId}/users`, { users })
      .then(async (response) => {
        const { data } = response;
        const bannerOptions = getAddUsersToWorkspaceResultBanner(
          users,
          workspaceId,
          currentLicensedUsers,
          data
        );

        dispatch(hideModal(modalTypes.ADD_USER_TO_WORKSPACE));
        dispatch(setWorkspacesInfo(data));
        dispatch(showGlobalAlert(bannerOptions));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const systemAdminRemoveUserFromWorkspace =
  (userName, workspaceId, workspaceName) => async (dispatch, getState) => {
    dispatch(pageLoading(true));
    const { adminType } = getState().systemAdmin;
    await localhost
      .delete(`/${adminType}/workspace/${workspaceId}/user`, {
        data: { userName },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(removeErrorMessage);
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(setWorkspacesInfo(data));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `${userName} successfully removed from ${workspaceName}`,
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const deleteWorkspace =
  (workspaceName, workspaceId) => async (dispatch, getState) => {
    dispatch(pageLoading(true));
    const { adminType } = getState().systemAdmin;
    await localhost
      .delete(`/${adminType}/workspace/${workspaceId}`)
      .then(async (response) => {
        const { data } = response;
        dispatch(hideModal(modalTypes.DELETE_WORKSPACE));
        dispatch(setWorkspacesInfo(data));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `${workspaceName} has been successfully deleted.`,
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.DELETE_WORKSPACE));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const renameWorkspace =
  (workspaceName, workspaceId) => async (dispatch, getState) => {
    dispatch(pageLoading(true));
    const { adminType } = getState().systemAdmin;
    await localhost
      .put(`/${adminType}/workspace/${workspaceId}`, {
        workspaceName,
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setWorkspacesInfo(data));
        dispatch(hideModal(modalTypes.TEXT_FIELD_MODAL));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: "Workspace name successfully updated",
          })
        );
        dispatch(renameSessionWorkspace({ workspaceId, workspaceName }));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const createWorkspace = (params) => async (dispatch, getState) => {
  dispatch(pageLoading(true));
  const { adminType } = getState().systemAdmin;
  await localhost
    .post(`/${adminType}/workspace`, null, {
      params,
    })
    .then(async (response) => {
      const { data } = response;
      dispatch(setWorkspacesInfo(data));
      if (adminType === "cloudAdmin") {
        dispatch(hideModal(modalTypes.CREATE_WORKSPACE_WITH_LICENSING));
      } else {
        dispatch(hideModal(modalTypes.TEXT_FIELD_MODAL));
      }
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `${params?.workspaceName} has been successfully created`,
        })
      );
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const setWorkspaceReservedLicenses =
  (workspaceId, reservedLicenses, licensedUsersExceedLimit) =>
  async (dispatch) => {
    dispatch(pageLoading(true));

    await localhost
      .patch(`/systemAdmin/workspace/${workspaceId}/reservedLicenses`, null, {
        params: { reservedLicenses },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setWorkspacesInfo(data));
        dispatch(hideModal(modalTypes.WORKSPACE_RESERVED_LICENSES));
        if (licensedUsersExceedLimit) {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.success,
              text: "Reserved Licenses successfully updated. The number of “Licensed Users” is now higher than the new reserved licenses. All “Licensed Users” have been deactivated. Please click 'Edit Permissions' to assign permissions to the desired members",
            })
          );
        } else {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.success,
              text: "Workspace reserved licenses successfully updated",
            })
          );
        }
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.WORKSPACE_RESERVED_LICENSES));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const setAnonymizeUsers = (anonymize) => async (dispatch) => {
  dispatch(pageLoading(true));

  await localhost
    .patch("/systemAdmin/anonymize", null, { params: { anonymize } })
    .then(async (response) => {
      const { data } = response;
      dispatch(setWorkspacesInfo(data));
      dispatch(hideModal(modalTypes.ANONYMIZE_USERS));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: "Anonymize Users successfully updated",
        })
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.ANONYMIZE_USERS));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const editUserPermissions =
  (
    workspaceId,
    username,
    licensed,
    dictionaries,
    readabilitySettings,
    acronymSettings,
    discoverySettings,
    excelOutputs,
    proposalStages,
    docStyleTemplates,
    editCurrencies,
    archiveStatusesAdmin,
    userAdmin
  ) =>
  async (dispatch, getState) => {
    dispatch(pageLoading(true));
    const currentUserName = getState().app?.session?.userInfo?.username;

    await localhost
      .put(`/systemAdmin/workspace/${workspaceId}/permissions`, {
        username,
        licensed,
        dictionaries,
        readabilitySettings,
        acronymSettings,
        discoverySettings,
        excelOutputs,
        proposalStages,
        docStyleTemplates,
        editCurrencies,
        archiveStatusesAdmin,
        userAdmin,
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setWorkspacesInfo(data));
        dispatch(hideModal(modalTypes.EDIT_PERMISSIONS));

        if (currentUserName === username) {
          dispatch(showModal(modalTypes.CONFIRMATION, permissionsChangedSelfModal(dispatch)));
        } else {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.success,
              text: "User has been successfully updated",
            })
          );
        }
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.EDIT_PERMISSIONS));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const importUsersFromCsvCloudAdmin = (formData) => async (dispatch) => {
  await localhost
    .post("/cloudAdmin/importUsers", formData, {
      headers: {
        "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
      },
    })
    .then(async (response) => {
      const { data } = response;
      dispatch(setWorkspacesInfo(data.workspacesResponse));
      dispatch(hideModal(modalTypes.IMPORT_USERS));
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          userAdminImportUsersFromCsvConfirmationModal(
            data.usersReportString,
            data.message,
            dispatch,
            true
          )
        )
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.IMPORT_USERS));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const exportUsageReport = (filter) => async (dispatch) => {
  dispatch(pageLoading(true));

  await localhost
    .get("/systemAdmin/usage", {
      responseType: "blob",
      params: { filter },
    })
    .then(async (response) => {
      const { data } = response;

      downloadFileFromResponse(data, "UsageReport.csv");

      dispatch(hideModal(modalTypes.EXPORT_REPORT));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const setPasswordExpiry =
  (workspaceId, passwordExpiryPeriod, handleSuccess) =>
  async (dispatch, getState) => {
    dispatch(pageLoading(true));
    const { adminType } = getState().systemAdmin;
    await localhost
      .post(
        `/${adminType}/updatePasswordExpiry`,
        {
          workspaceId,
          passwordExpiry: passwordExpiryPeriod,
        },
        {
          headers: {
            "Content-Type": URLENCODED_FORM_CONTENT_TYPE,
          },
        }
      )
      .then(() => {
        handleSuccess();
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };
